/**
 * Summary Card Component
 * Modern design with soft pastel icon backgrounds
 */

import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

// Soft pastel background colors
const bgColorMap: Record<string, string> = {
  primary: 'rgba(229, 115, 115, 0.15)',   // Soft coral/red
  success: 'rgba(129, 199, 132, 0.15)',   // Soft green
  warning: 'rgba(255, 183, 77, 0.15)',    // Soft orange
  error: 'rgba(229, 115, 115, 0.15)',     // Soft red
  info: 'rgba(100, 181, 246, 0.15)',      // Soft blue
  secondary: 'rgba(186, 104, 200, 0.15)', // Soft purple
};

// Icon colors (matching but stronger)
const iconColorMap: Record<string, string> = {
  primary: '#E57373',   // Coral
  success: '#81C784',   // Green
  warning: '#FFB74D',   // Orange
  error: '#E57373',     // Red
  info: '#64B5F6',      // Blue
  secondary: '#BA68C8', // Purple
};

interface SummaryCardProps {
  title: string;
  value: number | string;
  icon: React.ReactNode;
  color?: 'primary' | 'success' | 'warning' | 'error' | 'info' | 'secondary';
  subtitle?: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
  onClick?: () => void;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color = 'primary',
  subtitle,
  trend,
  loading = false,
  onClick
}) => {
  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        borderRadius: 3,
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          transform: onClick ? 'translateY(-2px)' : 'none',
        },
        transition: 'all 0.2s ease-in-out'
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography
              color="text.secondary"
              gutterBottom
              variant="body2"
              sx={{ fontWeight: 500, mb: 1 }}
            >
              {title}
            </Typography>
            {loading ? (
              <CircularProgress size={28} sx={{ my: 1 }} />
            ) : (
              <>
                <Typography
                  variant="h4"
                  component="div"
                  fontWeight="bold"
                  sx={{
                    fontSize: '1.75rem',
                    lineHeight: 1.2,
                    mb: 0.5
                  }}
                >
                  {value}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block' }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
            {trend && !loading && (
              <Box display="flex" alignItems="center" mt={1}>
                {trend.isPositive ? (
                  <TrendingUp fontSize="small" sx={{ color: '#81C784' }} />
                ) : (
                  <TrendingDown fontSize="small" sx={{ color: '#E57373' }} />
                )}
                <Typography
                  variant="body2"
                  sx={{
                    color: trend.isPositive ? '#81C784' : '#E57373',
                    fontWeight: 500,
                    ml: 0.5
                  }}
                >
                  {trend.isPositive ? '+' : ''}{trend.value}% from last month
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: bgColorMap[color] || bgColorMap.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '& svg': {
                color: iconColorMap[color] || iconColorMap.primary,
                fontSize: 24
              }
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
